import { useContext, useState, useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import { PB97T, PB70T } from '@h2/components/colors'
import { Box } from '@h2/components/layout/box'
import { Skeleton } from '@h2/components/loading/skeleton'
import { RetractIcon } from '@h2/components/icons/retract'
import { Button } from '@h2/components/buttons/button'
import { features } from '@h2/components/media'

import { Lesson } from './lesson'
import { Action } from './action'

import { subNavList } from '../mixins'
import { Overlay } from '../overlay'
import { Context } from '../../context'
import { useCallbackOnEventOutside } from '../../../hooks/useCallbackOnEventOutside'
import { useCallbackOnRouteChange } from '../../../hooks/useCallbackOnRouteChange'

const Wrap = styled.div`
  @media (${features.minWidth.laptop}) {
    position: relative;
    height: 100%;
    overflow-y: ${({ open }) => (open ? 'visible' : 'hidden')};
  }
`
const Lessons = styled.ul`
  ${subNavList};
  flex-direction: column;
  max-height: calc(100vh - ${({ navHeight }) => navHeight});
  overflow-y: auto;
  box-shadow: ${({ open }) => (open ? `1px 1px 3px ${PB70T}` : '')};
  > li {
    display: flex;
    align-items: center;
    min-height: 51px;
    padding: 7px 10px;
    background-color: ${PB97T};
    border: none;
    border-top: 1px solid white;

    &:last-child {
      border-bottom: none;
    }
  }

  @media (${features.minWidth.laptop}) {
    top: 0;
    bottom: auto;
    z-index: 2;
    max-height: 100vh;
    overflow-y: ${({ open }) => (open ? 'auto' : 'hidden')};

    > li {
      padding-left: ${({ count }) => (count ? '40px' : '10px')};

      &:first-child {
        border-top: none;
        position: relative;
        padding-left: 10px;
      }
    }
  }
`
const BoxDropdown = styled(Box)`
  display: none;

  @media (${features.minWidth.laptop}) {
    display: block;
    margin: 0 5px 0 0;
    min-width: 25px;
  }
`
const BoxLesson = styled(Box)`
  width: 100%;
  min-width: 0;
`
const BoxAction = styled(Box)`
  margin: 0 0 0 15px;
  flex: 0 0 120px;
  text-align: right;
`
const ButtonDropdown = styled(Button)`
  && {
    background-color: transparent;
    padding: 0;
    line-height: normal;

    > span {
      display: flex;
      flex-direction: column;
      align-items: center;
      letter-spacing: -0.5px;
      width: 100%;

      > span:first-child {
        color: black;
        font-size: 9px;
        position: relative;
        top: 4px;
      }
    }
  }
`
const Retract = styled(RetractIcon)`
  color: #dadada;
  cursor: pointer;
  transition: transform 0.25s ease-in-out;
  transform: ${({ open }) => (open ? 'rotate(0deg)' : 'rotate(180deg)')};

  > svg {
    width: 20px;
    height: 20px;
  }
`
const renderLesson = lesson => {
  return (
    <>
      <BoxLesson>
        <Lesson lesson={lesson} />
      </BoxLesson>
      <BoxAction>
        <Action lesson={lesson} />
      </BoxAction>
    </>
  )
}
const ActiveLessons = ({ lessons }) => {
  const ref = useRef()
  const { navHeight, isMenuFetching } = useContext(Context)
  const [open, setOpen] = useState(false)
  const handleOnClick = useCallback(() => {
    setOpen(prevOpen => !prevOpen)
  }, [])
  const closeLessons = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  useCallbackOnEventOutside(
    useMemo(() => [ref], [ref]),
    closeLessons
  )

  useCallbackOnRouteChange(closeLessons)

  return (
    <>
      <Overlay navHeight={navHeight} />
      <Wrap open={open} ref={ref}>
        <Lessons
          offset={navHeight}
          navHeight={navHeight}
          count={lessons.length}
          open={open}>
          {lessons
            .filter((lesson, idx) => idx === 0 || open)
            .map((lesson, idx) => (
              <li key={lesson.id}>
                {idx === 0 ? (
                  isMenuFetching ? (
                    <Skeleton height="25px" />
                  ) : (
                    <>
                      <BoxDropdown>
                        <ButtonDropdown onClick={handleOnClick}>
                          <span>1 / {lessons.length}</span>
                          <Retract open={open} />
                        </ButtonDropdown>
                      </BoxDropdown>
                      <BoxLesson>
                        <Lesson lesson={lesson} />
                      </BoxLesson>
                      <BoxAction>
                        <Action lesson={lesson} />
                      </BoxAction>
                    </>
                  )
                ) : (
                  renderLesson(lesson)
                )}
              </li>
            ))}
        </Lessons>
      </Wrap>
    </>
  )
}

ActiveLessons.propTypes = {
  lessons: PropTypes.array.isRequired
}

export { ActiveLessons }
